'use client'
import { type FC, useState } from 'react'

import { F0Box, F0Icon } from '@factorialco/f0-react'
import { ChevronLeft, ChevronRight } from '@factorialco/f0-react/icons/app'
import { useProductCatalog } from 'modules/it_management/lib/graphqlProductCatalogContext'

interface DeviceImageShowcaseProps {
  images: string[]
  activeIndex: number
  onClose: () => void
}

export const DeviceImageShowcase: FC<DeviceImageShowcaseProps> = ({
  images,
  activeIndex: initialIndex,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const { isProductCatalogUrl } = useProductCatalog()

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }

  const currentImage = images[currentIndex] ?? ''

  return (
    // Backdrop overlay
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      {/* Content container — stop click propagation */}
      <div
        className="relative flex flex-col items-center gap-6 max-w-[80vw] max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main image with chevron navigation */}
        <div className="relative flex items-center">
          {/* Left chevron */}
          {images.length > 1 && (
            <button
              className="absolute left-[-48px] p-2 rounded-full bg-white/80 hover:bg-white cursor-pointer z-10"
              onClick={goToPrev}
              aria-label="Previous image"
            >
              <F0Icon icon={ChevronLeft} size="md" />
            </button>
          )}

          {/* Large image */}
          <img
            src={currentImage}
            alt={`image-${currentIndex + 1}`}
            crossOrigin={isProductCatalogUrl(currentImage) ? 'use-credentials' : undefined}
            className="object-contain rounded-lg max-h-[70vh] max-w-[75vw]"
          />

          {/* Right chevron */}
          {images.length > 1 && (
            <button
              className="absolute right-[-48px] p-2 rounded-full bg-white/80 hover:bg-white cursor-pointer z-10"
              onClick={goToNext}
              aria-label="Next image"
            >
              <F0Icon icon={ChevronRight} size="md" />
            </button>
          )}
        </div>

        {/* Horizontal thumbnail strip */}
        {images.length > 1 && (
          <F0Box flexDirection="row" gap="sm" justifyContent="center">
            {images.map((src, idx) => (
              <div key={idx} className="cursor-pointer" onClick={() => setCurrentIndex(idx)}>
                <F0Box
                  border="default"
                  borderRadius="sm"
                  borderColor={idx === currentIndex ? 'bold' : undefined}
                  padding="xs"
                >
                  <img
                    src={src}
                    alt={`thumbnail-${idx + 1}`}
                    crossOrigin={isProductCatalogUrl(src) ? 'use-credentials' : undefined}
                    className="w-12 h-12 object-contain"
                  />
                </F0Box>
              </div>
            ))}
          </F0Box>
        )}
      </div>
    </div>
  )
}
